import React, {useCallback, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import {useSelector} from 'react-redux';
import {HiOutlineDownload} from '@react-icons/all-files/hi/HiOutlineDownload';
import {HiOutlineChevronDown} from '@react-icons/all-files/hi/HiOutlineChevronDown';
import {HiOutlineChevronUp} from '@react-icons/all-files/hi/HiOutlineChevronUp';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Grow from '@material-ui/core/Grow';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import Popper from '@material-ui/core/Popper';
import MenuList from '@material-ui/core/MenuList';
import {AppOutlinedButton} from '../../../../../Core/buttons/AppOutlinedButton/AppOutlinedButton';
import {selectTransaction} from '../../../../../SignerUi/signerUiSlice';
import {getIsDownloadAllowed} from '../../../../../SignerUi/utils/selectors';
import {useDownloadDocument} from '../../../../../../hooks/useDownloadDocument';
import './downloadAllButton.less';

const PFD_TYPE = 'PDF';
const ZIP_TYPE = 'ZIP';
const downloadTypes = [ZIP_TYPE, PFD_TYPE];

export default function DownloadAllButton({
    documents
}) {
    const intl = useIntl();
    const [open, setOpen] = useState(false);
    const anchorRef = useRef(null);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const selectedDownloadType = downloadTypes[selectedIndex];
    const transaction = useSelector(selectTransaction);

    const {isDownloading, downloadPackage, downloadSignedDocument} = useDownloadDocument({
        transactionId: transaction?.id
    });

    const handleDownloadAllZip = useCallback(async () => {
        if (!transaction?.id) return;

        await downloadPackage(`transaction-${transaction.id}.zip`);
    }, [transaction?.id, downloadPackage]);

    async function handleDownloadAllPdf() {
        if (!transaction?.id || !documents?.length) return;

        /* eslint-disable no-await-in-loop */
        for (const document of documents) {
            await downloadSignedDocument(document.id, `${document.name}.pdf`);
            await new Promise(resolve => setTimeout(resolve, 500));
        }
        /* eslint-enable no-await-in-loop */
    }

    function handleDownload() {
        if (selectedDownloadType === PFD_TYPE) {
            return handleDownloadAllPdf();
        }

        return handleDownloadAllZip();
    }

    function handleMenuItemClick(event, index) {
        setSelectedIndex(index);
        setOpen(false);
    }

    function handleToggle() {
        setOpen((prevOpen) => !prevOpen);
    }

    function handleClose(event) {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }

        setOpen(false);
    }
    if (!getIsDownloadAllowed(transaction)) return null;

    return (
        <div className="signing-menu-documents-download-btn-container">
            <ButtonGroup variant="contained" disabled={isDownloading}>
                <AppOutlinedButton
                    classes={{root: 'signing-menu-documents-download-btn'}}
                    endIcon={<HiOutlineDownload className="icon" />}
                    onClick={handleDownload}
                >
                    {intl.formatMessage({id: 'oss.components.SideBar.Download.all', defaultMessage: ''})}
                </AppOutlinedButton>
                <AppOutlinedButton
                    classes={{root: 'signing-menu-documents-download-type-select'}}
                    size="small"
                    ref={anchorRef}
                    onClick={handleToggle}
                >
                    {open
                        ? <HiOutlineChevronUp className="chevron-icon" />
                        : <HiOutlineChevronDown className="chevron-icon" />}
                    {selectedDownloadType}
                </AppOutlinedButton>
            </ButtonGroup>
            <Popper
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
            >
                {({TransitionProps, placement}) => (
                    <Grow
                        {...TransitionProps}
                        style={{
                            transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom'
                        }}
                    >
                        <Paper className="extension-menu-paper">
                            <ClickAwayListener onClickAway={handleClose}>
                                <MenuList autoFocusItem>
                                    {downloadTypes.map((type, index) => (
                                        <MenuItem
                                            key={type}
                                            selected={index === selectedIndex}
                                            onClick={(event) => handleMenuItemClick(event, index)}
                                            className="extension-menu-item"
                                        >
                                            {type}
                                        </MenuItem>
                                    ))}
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </div>
    );
}
